import { StatusEnum, useGetAllClientContactQuery } from '@codezo/graphql';
import { useMemo } from 'react';

export interface NominableClientContact {
	id: string;
	name: string;
	email?: string | null;
	phone?: string | null;
	/** Invited but not signed up yet — still nominable. */
	pending: boolean;
}

/**
 * The contacts at a client that can be chosen as a quote's client contacts.
 * Guests and cancelled contacts are left out: the backend refuses both
 * (guests are always emailed anyway). A client organisation is small enough
 * to load in one page and filter locally.
 */
export function useNominableClientContacts(organizationId: string, skip = false) {
	const { data, loading } = useGetAllClientContactQuery({
		variables: {
			input: {
				organizationId,
				isGuest: false,
				pagination: { currentPage: 1, limit: 200 },
			},
		},
		fetchPolicy: 'no-cache',
		skip: skip || !organizationId,
	});

	const contacts = useMemo<NominableClientContact[]>(
		() =>
			(data?.getAllClientContact?.data ?? [])
				.filter((c) => !c.isGuest && c.status !== StatusEnum.Canceled)
				.map((c) => ({
					id: c.id,
					name:
						[c.firstName, c.lastName].filter(Boolean).join(' ') ||
						c.authenticationUser?.email ||
						'Unnamed contact',
					email: c.authenticationUser?.email,
					phone: c.phone,
					pending: c.status === StatusEnum.Pending,
				})),
		[data]
	);

	return { contacts, loading };
}
